import { ChevronDown, Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from 'components/blocks/language-selector/language-selector';
import { useTheme } from 'components/core/theme-provider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { cn } from 'lib/utils';

type ThemeOption = 'system' | 'dark' | 'light';

const THEME_ICONS = {
  system: Monitor,
  dark: Moon,
  light: Sun,
} as const;

export function KlaxAuthTopBar() {
  const { selectedTheme, setTheme, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { t } = useTranslation();

  const options: { value: ThemeOption; label: string }[] = [
    { value: 'system', label: t('SYSTEM', 'System') },
    { value: 'dark', label: t('DARK', 'Dark') },
    { value: 'light', label: t('LIGHT', 'Light') },
  ];

  const currentOption = options.find((option) => option.value === selectedTheme) ?? options[0];
  const CurrentIcon = THEME_ICONS[currentOption.value];

  return (
    <div
      className={cn(
        'flex h-10 items-center gap-1 rounded-[32px] bg-white pl-2 pr-3',
        isDark && 'bg-[#1d1d1d]'
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('APPEARANCE', 'Appearance')}
            className="flex cursor-pointer items-center justify-center gap-1 px-3 py-2"
          >
            <CurrentIcon
              className="size-4 shrink-0 text-black dark:text-true-white"
              aria-hidden
            />
            <span className="text-sm font-normal leading-[1.2] text-black dark:text-true-white">
              {currentOption.label}
            </span>
            <ChevronDown
              className="size-4 shrink-0 text-black dark:text-true-white"
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-[140px] rounded-[12px] border-border bg-background p-1.5 shadow-xl"
        >
          {options.map((option) => {
            const Icon = THEME_ICONS[option.value];
            const isSelected = selectedTheme === option.value;

            return (
              <DropdownMenuItem
                key={option.value}
                className={cn(
                  '!text-sm !leading-[22px] h-auto cursor-pointer justify-between rounded-[6px] px-3 py-[6px] font-normal focus:bg-accent',
                  isSelected ? 'text-primary-600' : 'text-typography-grey-1-secondary'
                )}
                onClick={() => setTheme(option.value)}
              >
                <span>{option.label}</span>
                <Icon className="h-4 w-4" />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <LanguageSelector variant="klax" />
    </div>
  );
}
